import { useEffect, useRef, useState } from "react";
import { RefreshCw, FileText, Smartphone, Columns2 } from "lucide-react";
import { api } from "@/lib/api";

const A4W = 900;

const A4Frame = ({ html }) => {
  const box = useRef(null);
  const [k, setK] = useState(0.6);
  useEffect(() => {
    const ro = new ResizeObserver(([e]) => setK(Math.min(1, e.contentRect.width / A4W)));
    ro.observe(box.current);
    return () => ro.disconnect();
  }, []);
  return (
    <div ref={box} className="flex-1 min-w-0 h-[70vh] min-h-[700px] overflow-hidden" data-testid="a4-preview">
      <iframe title="Document preview" srcDoc={html} style={{ width: A4W, height: `calc((max(70vh, 700px)) / ${k})`, transform: `scale(${k})`, transformOrigin: "0 0" }} className="border-0 bg-[#E7E1D6]" data-testid="preview-iframe" />
    </div>
  );
};
const MODES = [
  { k: "both", label: "Side by side", icon: Columns2 },
  { k: "a4", label: "A4 pages", icon: FileText },
  { k: "phone", label: "Phone", icon: Smartphone },
];

const PhoneFrame = ({ html }) => (
  <div className="shrink-0 mx-auto" data-testid="phone-preview">
    <div className="relative w-[352px] h-[700px] rounded-[44px] bg-[#1E1A1B] p-[12px] shadow-[0_18px_40px_rgba(42,35,37,.28)]">
      <div className="absolute top-[18px] left-1/2 -translate-x-1/2 w-24 h-5 rounded-full bg-black z-10" />
      <div className="w-full h-full rounded-[32px] overflow-hidden bg-[#FBF7EF]">
        <iframe title="Phone preview" srcDoc={html} style={{ width: 390, height: 804, transform: "scale(0.841)", transformOrigin: "0 0" }} className="border-0" data-testid="phone-preview-iframe" />
      </div>
    </div>
    <div className="text-center text-[11px] text-[#5C5255] mt-2">Phone edition · 390 px wide</div>
  </div>
);

export const PreviewPane = ({ productId, version }) => {
  const [html, setHtml] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("both");

  const load = () => {
    setLoading(true);
    api.get(`/products/${productId}/preview`, { responseType: "text" })
      .then((r) => setHtml(r.data))
      .finally(() => setLoading(false));
  };

  useEffect(load, [productId, version]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="bg-[#E7E1D6] border border-[#2A2325]/10" data-testid="preview-pane">
      <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-[#2A2325]/10 bg-[#FFFDF9]">
        <span className="eyebrow">Live document preview</span>
        <div className="flex items-center gap-3">
          <div className="flex border border-[#2A2325]/15" role="tablist">
            {MODES.map(({ k, label, icon: Icon }) => (
              <button key={k} onClick={() => setMode(k)} aria-pressed={mode === k} className={`text-xs px-2.5 py-1.5 flex items-center gap-1.5 transition-colors ${mode === k ? "bg-[#5E2B38] text-[#FFFDF9]" : "text-[#5E2B38] hover:bg-[#F5EFE6]"}`} data-testid={`preview-mode-${k}`}>
                <Icon size={12} /> {label}
              </button>
            ))}
          </div>
          <button onClick={load} className="text-xs flex items-center gap-1.5 text-[#5E2B38] hover:underline" data-testid="preview-refresh-btn">
            <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
          </button>
        </div>
      </div>
      <div className={`flex ${mode === "both" ? "flex-col xl:flex-row" : ""} gap-5 ${mode !== "a4" ? "p-4" : ""}`}>
        {mode !== "phone" && (
          <A4Frame html={html} />
        )}
        {mode !== "a4" && <PhoneFrame html={html} />}
      </div>
    </div>
  );
};
